import React from 'react';

/**
 * StatusBadge: the one status pill of the app. Every loan status has one
 * colour everywhere (STATUS_TONES); other statuses (active/inactive and the
 * like) fall back to a colour from their words.
 *
 * Props:
 *   status    {string}  the status text, shown as it is
 *   label     {string}  optional text to show instead of the status
 *   size      {string}  'small' | 'default' | 'large'
 *   className {string}
 */

// [dot, text, background] per tone, light and dark.
const TONES = {
  blue: ['bg-blue-500', 'text-blue-700 dark:text-blue-300', 'bg-blue-50 dark:bg-blue-900/30'],
  indigo: ['bg-indigo-500', 'text-indigo-700 dark:text-indigo-300', 'bg-indigo-50 dark:bg-indigo-900/30'],
  amber: ['bg-amber-500', 'text-amber-800 dark:text-amber-300', 'bg-amber-50 dark:bg-amber-900/30'],
  green: ['bg-emerald-500', 'text-emerald-700 dark:text-emerald-300', 'bg-emerald-50 dark:bg-emerald-900/30'],
  orange: ['bg-orange-500', 'text-orange-700 dark:text-orange-300', 'bg-orange-50 dark:bg-orange-900/30'],
  red: ['bg-rose-500', 'text-rose-700 dark:text-rose-300', 'bg-rose-50 dark:bg-rose-900/30'],
  gray: ['bg-gray-400', 'text-gray-600 dark:text-slate-300', 'bg-gray-100 dark:bg-slate-700'],
};

// Each loan status's colour, the same on every screen: in progress blue,
// paid out indigo, being repaid amber, finished green, lost orange/red,
// stopped grey.
export const STATUS_TONES = {
  'pending': 'blue',
  'awaiting client': 'blue',
  'awaiting staff': 'blue',
  'awaiting approval': 'blue',
  'approved': 'indigo',
  'outstanding': 'amber',
  'paid off': 'green',
  'bad debt': 'orange',
  'written off': 'red',
  'rejected': 'red',
  'cancelled': 'gray',
  'terminated': 'gray',
  // Other records' statuses.
  'active': 'green',
  'inactive': 'red',
  'awaiting update': 'amber',
  'resubmitted': 'blue',
  'yes': 'green',
  'no': 'gray',
  // Support tickets, compliance cases, flagged documents.
  'open': 'amber',
  'in progress': 'blue',
  'in_progress': 'blue',
  'resolved': 'green',
  'closed': 'gray',
  'dismissed': 'gray',
  'genuine': 'green',
  'tampered': 'red',
  // Messages sent to clients.
  'sent': 'green',
  'failed': 'red',
};

// A status not in the list: a tone from its words.
const toneFromWords = (s) => {
  if (/approved|active|collected|resolved|closed|genuine/.test(s)) return 'green';
  if (/rejected|failed|missed|tampered/.test(s)) return 'red';
  if (/overdue|warning|awaiting|review|open/.test(s)) return 'amber';
  if (/pending|processing|progress/.test(s)) return 'blue';
  return 'gray';
};

/** The [dot, text, background] classes of a status, for a place that cannot use the badge. */
export const statusClasses = (status) => {
  const s = String(status || '').trim().toLowerCase();
  return TONES[STATUS_TONES[s] || toneFromWords(s)];
};

const sizeMap = {
  small: { pill: 'px-2 py-0.5 text-[10px] gap-1', dot: 'w-1.5 h-1.5' },
  default: { pill: 'px-2.5 py-0.5 text-xs gap-1.5', dot: 'w-2 h-2' },
  large: { pill: 'px-3 py-1 text-sm gap-1.5', dot: 'w-2 h-2' },
};

const StatusBadge = ({ status, label = null, className = '', size = 'default', title }) => {
  const [dotCls, textCls, bgCls] = statusClasses(status);
  const { pill, dot } = sizeMap[size] ?? sizeMap.default;
  return (
    <span title={title} className={`inline-flex items-center whitespace-nowrap rounded-full font-semibold ${pill} ${textCls} ${bgCls} ${className}`}>
      <span className={`rounded-full flex-shrink-0 ${dot} ${dotCls}`} />
      {label ?? (status || 'Unknown')}
    </span>
  );
};

export default StatusBadge;
